import { computed } from 'vue'

import { defineComposable } from '@/core/utils/defineComposable'

import { usePlanStore } from './usePlanStore'

export const BillingType = {
  CBB: 'cbb',
  LBB: 'lbb',
  Unknown: 'unknown',
} as const

export type BillingType = (typeof BillingType)[keyof typeof BillingType]

/**
 * The intended single source of truth for determining the type of billing a customer is on.
 *
 * CBB - Classic BillingV3 credit-based, where the customer has an amount of annotation credits
 * and storage they can use before they get locked out. Technically self-serve, but in practic,
 * the sales team works around the faulty systema and manages plans directly on stripe.
 *
 * LBB - More modern license based billing, based on plans defined on the backend. Plas serve as
 * templates customers are put on, but can be modified for every specific customer. No lockouts
 * actually happen on this plan.
 *
 * SelfServe - A customer on a free LBB plan tier, that can upgrade to a Starter plan on their own,
 * where hard lockouts based on usage will exist and certain features will be gated.
 */
export const useBillingType = defineComposable(() => {
  const planStore = usePlanStore()

  return computed<BillingType>(() => {
    if (!planStore.hasCustomerPlanLoaded) {
      return BillingType.Unknown
    }

    return planStore.customerPlan ? BillingType.LBB : BillingType.CBB
  })
})
